import { Modal } from "react-bootstrap";
import { toast } from "react-toastify";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { deletePartnerTeam, getTeamDeletePreview } from "../../../services/PartnerService";
import { showMutationError } from "../../../utils/showMutationError";

const DeleteTeamModal = ({ show, onHide, team, partnerId }) => {
  const queryClient = useQueryClient();

  const {
    data: previewData,
    isLoading: previewLoading,
    isError: previewIsError,
  } = useQuery({
    queryKey: ["teamDeletePreview", partnerId, team?.id],
    queryFn: () => getTeamDeletePreview(partnerId, team.id),
    enabled: !!(show && partnerId && team?.id),
    staleTime: 0,
  });

  const preview = previewData?.data?.data ?? null;
  const membersCount = preview?.members_count ?? 0;
  const pendingApprovalsCount = preview?.pending_approvals_count ?? 0;
  const reviewers = preview?.reviewers ?? [];

  const membersBlock = membersCount > 0;
  const approvalsBlock = pendingApprovalsCount > 0;
  const isBlocked = membersBlock || approvalsBlock;

  const deleteMutation = useMutation({
    mutationFn: () => deletePartnerTeam(partnerId, team.id),
    onSuccess: () => {
      toast.success(`Team ${team.name} deleted.`);
      queryClient.invalidateQueries({ queryKey: ["partnerTeams", partnerId] });
      queryClient.invalidateQueries({ queryKey: ["allPartnerTeams", partnerId] });
      queryClient.invalidateQueries({ queryKey: ["allPartnerTeamsForSetupNudge", partnerId] });
      queryClient.invalidateQueries({ queryKey: ["usersWhoCanBeAssignedAsReviewers", partnerId] });
      onHide();
    },
    onError: (error) => {
      showMutationError(error, "Could not delete this team. Please try again.");
    },
  });

  const submitting = deleteMutation.isPending;
  const canConfirm = !previewLoading && !previewIsError && !isBlocked && !submitting;

  const confirmLabel = membersBlock
    ? "Reassign members first"
    : approvalsBlock
      ? "Resolve pending approvals first"
      : "Delete team";

  return (
    <Modal show={show} onHide={onHide} centered scrollable>
      <div className="modal-content">
        <div className="modal-header">
          <h5 className="modal-title">Delete Team</h5>
          <button type="button" onClick={onHide} className="btn-close" disabled={submitting} />
        </div>
        <div className="modal-body">
          {previewLoading ? (
            <div className="text-center py-3">
              <div className="spinner-border spinner-border-sm text-primary" role="status">
                <span className="visually-hidden">Loading team details</span>
              </div>
            </div>
          ) : previewIsError ? (
            <p className="text-danger mb-0">
              Could not load this team's details. Please close this dialog and try again.
            </p>
          ) : (
            <>
              <p className="fs-16 mb-3">
                You are about to delete <strong>{team?.name}</strong>.
              </p>

              {membersBlock && (
                <div className="alert alert-warning py-2 mb-2">
                  <i className="fa fa-exclamation-triangle me-2" />
                  This team still has {membersCount} {membersCount === 1 ? "member" : "members"}.
                  Reassign {membersCount === 1 ? "that member" : "them"} to another team before deleting.
                </div>
              )}

              {approvalsBlock && (
                <div className="alert alert-warning py-2 mb-2">
                  <i className="fa fa-exclamation-triangle me-2" />
                  This team has {pendingApprovalsCount} pending {pendingApprovalsCount === 1 ? "approval" : "approvals"} awaiting review.
                  Resolve {pendingApprovalsCount === 1 ? "it" : "them"} before deleting.
                </div>
              )}

              {reviewers.length > 0 && (
                <div className="mb-2">
                  <p className="fw-semibold mb-1">Reviewer changes on this team:</p>
                  <ul className="mb-0 ps-3" style={{ overflowWrap: "anywhere" }}>
                    {reviewers.map((reviewer) => (
                      <li key={reviewer.id} className="mb-1">
                        <strong>{reviewer.name}</strong>
                        {" "}
                        {reviewer.will_lose_review_permissions
                          ? "will lose reviewer permissions across the app because this is their only team."
                          : "will keep their reviewer permissions because they still review another team."}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {!isBlocked && (
                <p className="text-danger font-w600 mb-0">This action cannot be undone.</p>
              )}
            </>
          )}
        </div>
        <div className="modal-footer">
          <button type="button" className="btn btn-secondary" onClick={onHide} disabled={submitting}>
            Cancel
          </button>
          <button
            type="button"
            className="btn btn-danger"
            onClick={() => deleteMutation.mutate()}
            disabled={!canConfirm}
          >
            {submitting ? (
              <>
                <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true" />
                Deleting
              </>
            ) : (
              confirmLabel
            )}
          </button>
        </div>
      </div>
    </Modal>
  );
};

export default DeleteTeamModal;
